import { getClientDropDown } from "@/api/clients/clients.queries";
import { getEntityDropdown } from "@/api/entities/entities.queries";
import { getServiceProviderDropDown } from "@/api/service-provider/tableDropdown/serviceProviderTableDropdown.queries";
import { getWODropdown } from "@/api/work-orders/work-orders.queries";
import { getUserDropdown } from "@/api/user-management/userManagement.queries";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRolesDropdown } from "@/api/roles-permissions/roles-permissions.queries";
import { getBrandDropdown } from "@/api/clients/Brands/brands.queries";
import { getLocationFilterDropdown } from "@/api/clients/locations/locations.queries";
import { getContactsTableDropdown } from "@/api/contacts/contacts.queries";
import { getFormBuilderDropdown } from "@/api/form-builder/formBuilder.queries";

export const useTableDropdown = ({
  searchTerm,
  searchParams,
  enabled = true,
  storeType = "clients",
}: {
  searchTerm: string;
  searchParams?: TSearchParams;
  label: string;
  enabled?: boolean;
  storeType?:
    | "clients"
    | "entities"
    | "serviceProviders"
    | "assets"
    | "workOrders"
    | "users"
    | "roles"
    | "brands"
    | "locations"
    | "contacts"
    | "forms";
}) => {
  // Select the appropriate API function based on storeType
  const getDropdownData = (signal: AbortSignal) => {
    const type = searchParams?.type || "";
    const search = searchTerm || undefined;
    const key = searchParams?.key;

    switch (storeType) {
      case "clients":
        return getClientDropDown(type, search, signal, key, searchParams);
      case "entities":
        return getEntityDropdown(type, search, signal, key);
      case "serviceProviders":
        // TODO: Add service provider dropdown API when available
        return getServiceProviderDropDown(type, search, signal, key, searchParams);
      case "assets":
        // TODO: Add assets dropdown API when available
        return getClientDropDown(type, search, signal, key);
      case "workOrders":
        return getWODropdown(type, search, signal, key, searchParams);
      case "users":
        return getUserDropdown(type, search, signal, key);
      case "roles":
        return getRolesDropdown(type, search, signal, key);
      case "brands":
        return getBrandDropdown(type, search, signal, key, searchParams);
      case "locations":
        return getLocationFilterDropdown(type, search, signal, key, searchParams);
      case "contacts":
        return getContactsTableDropdown(type, search, signal, key, searchParams);
      case "forms":
        return getFormBuilderDropdown(type, search, signal, key, searchParams);
      default:
        return getClientDropDown(type, search, signal, key);
    }
  };

  return useQuery({
    queryKey: [`${storeType}-dropdown`, searchParams?.type, searchTerm, searchParams?.key],
    queryFn: ({ signal }) => getDropdownData(signal),
    placeholderData: keepPreviousData,
    enabled: !!searchParams && !!searchParams.type && enabled,
  });
};
